import Popper from "popper.js";
import {Controller} from "stimulus"
import {addEvent, getFirstFocusableChild, hasParentNode, removeEvent, toggleAttribute} from "../support/util";

export default class extends Controller {
    static targets = ["anchor", "popup"];

    initialize() {
        const anchor = this.anchorTarget;
        const popup = this.popupTarget;
        const placement = this.anchorPosition;
        const offset = this.offset;
        let modifiers = {
            offset: {
                offset: offset,
            },
        };
        // Disable flip
        if (this.disableFlip) {
            modifiers = {
                ...modifiers,
                flip: {
                    enabled: false,
                }
            }
        }
        if (this.innerPopper) {
            modifiers = {
                ...modifiers, ...{
                    inner: {enabled: true},
                    preventOverflow: {
                        enabled: false,
                        padding: 0
                    },
                    hide: {enabled: false},
                }
            }
        }
        let options = {
            placement,
            positionFixed: true,
            modifiers: modifiers,
        }
        if (this.resizeToAnchor) {
            options = {
                ...options, ...{
                    onUpdate: this.setWidth,
                    onCreate: this.setWidth,
                }
            }
            options.modifiers = {...options.modifiers, ...{
                    preventOverflow: {
                        enabled: false,
                        padding: 0
                    },
                    hide: {enabled: false},
                }}
        }
        this.popper = new Popper(anchor, popup, options);
        this.isOpen = false;
    }

    setWidth({instance: {reference, popper}}) {
        popper.style.width = `${reference.offsetWidth}px`;
    }

    connect() {
        addEvent(document, 'keyup', this.onEscapeCallback);
    }

    disconnect() {
        this.popper.destroy();
        removeEvent(document, 'keyup', this.onEscapeCallback);
    }

    onEscapeCallback = event => {
        if (event.keyCode === 27 && this.isOpen) {
            this.toggleVisibility(event);
        }
    }

    clickAway = e => {
        if ((this.persistPopup && hasParentNode(e.target, this.popupTarget)) ||
            this.popupTarget.classList.contains(this.popupVisibleClass) &&
            hasParentNode(e.target, this.anchorTarget)) return;
        this.popupTarget.classList.remove(this.popupVisibleClass);
        this.anchorTarget.classList.remove('selected');
        toggleAttribute(this.popupTarget, 'aria-expanded');
        this.setIsOpen(false);
    }

    setIsOpen(isOpen) {
        this.isOpen = isOpen;
        if (isOpen) {
            addEvent(document, 'click', this.clickAway, true);
        } else {
            removeEvent(document, 'click', this.clickAway, true);
        }
        window.dispatchEvent(
            new CustomEvent(
                "se-popup-visibility-toggled",
                {
                    detail: {
                        popupId: this.popupId,
                        isOpen: isOpen
                    }
                }
            )
        );
    }

    toggleVisibility(e, force) {
        const ignore =
            e.target.dataset.popupIgnoreMeAsToggleVisibilityEventSource ?
                JSON.parse(e.target.dataset.popupIgnoreMeAsToggleVisibilityEventSource) :
                false;
        if (ignore || (!force && e.keyCode && !(e.keyCode === 13 || e.keyCode === 32))) return;

        e.stopPropagation();
        if (this.doPreventDefaultOnToggle) {
            e.preventDefault();
        }

        this.popupTarget.classList.toggle(this.popupVisibleClass);
        this.anchorTarget.classList.toggle('selected');
        toggleAttribute(this.popupTarget, 'aria-expanded');
        this.popper.update();
        this.setIsOpen(!this.isOpen);
        if (this.isOpen && !this.skipFocusingChildrenOnOpen) {
            const firstFocusableChildElement = getFirstFocusableChild(this.popupTarget);
            if (firstFocusableChildElement) {
                firstFocusableChildElement.focus();
            }
        } else {
            this.anchorTarget.focus();
        }
    }

    toggleVisibilityOff(e) {
        if (this.isOpen) {
            this.toggleVisibility(e, true);
        }
    }

    toggleVisibilityOn(e) {
        if (!this.isOpen) {
            this.toggleVisibility(e, true)
        }
    }

    hideOnGoForward(e) {
        if (e.keyCode === 9 && !e.shiftKey) {
            this.toggleVisibilityOff(e);
        }
    }

    // if you need to identify your popup
    get popupId() {
        return this.data.get("id");
    }

    // property for making the popup visible even if the item in it is clicked
    get persistPopup() {
        return this.data.get("persist");
    }

    // property for preventing default on toggle events
    get doPreventDefaultOnToggle() {
        return this.data.get("doPreventDefaultOnToggle") || false;
    }

    get anchorPosition() {
        return this.data.get("position") || 'bottom-end';
    }

    get innerPopper() {
        return this.data.get("innerPopper") || false;
    }

    get resizeToAnchor() {
        return this.data.get("resizeToAnchor") || false;
    }

    get offset() {
        return this.data.get("offset") || '0px, 8px';
    }

    get popupVisibleClass() {
        return this.data.get("visibleClass") || 'popup--visible';
    }

    get skipFocusingChildrenOnOpen() {
        return this.data.get("skipFocusingChildrenOnOpen") || false;
    }

    get disableFlip() {
        return this.data.get("disableFlip") || false;
    }
}
